import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePlaceholderDirective } from '@xilo-mono/shared/datepicker-i18n';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormViewerComponent } from './form-viewer.component';
import { RepeatTypeComponent } from './components/repeat/repeat.type';
import { CustomCheckboxComponent } from './components/checkbox/checkbox.component';
import { FormViewerQuestionPanelWrapperComponent } from './components/wrappers/question-wrapper/question-wrapper.component';
import { CustomSelectComponent } from './components/select/select.component';
import { CustomerFormTypeComponent } from './components/form/customer-form.component';
import { CustomerSectionTypeComponent } from './components/customer-section/customer-section.component';
import { QuestionGroupTypeComponent } from './components/question-group/question-group.component';
import { AddressComponent } from './components/address/address.component';
import { AddressV2Component } from './components/address-v2/address-v2.component';
import { CustomInputComponent } from './components/input/input.component';
import { PhoneNumberComponent } from './components/phonenumber/phonenumber.component';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { IntakeSectionTypeComponent } from './components/intake-section/intake-section.component';
import { FormViewerSectionPanelWrapperComponent } from './components/wrappers/intake-section-wrapper/section-wrapper.component';
import { FormIntakeTypeComponent } from './components/form/intake-form.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { RepeatTitlePipe } from './pipes/repeat-title.pipe';
import { SubmissionEffects } from './+state/submission/submission.effects';
import { metaReducers } from './+state/meta-reducers';
import { IntakeRepeatTypeComponent } from './components/repeat/intake-repeat/intake-repeat.type';
import { CustomTextareaComponent } from './components/textarea/textarea.component';
import { CustomTableComponent } from './components/table/table.component';
import { CustomRadioComponent } from './components/radio/radio.component';
import { CustomLastNameComponent } from './components/last-name/last-name.component';
import { CustomFirstNameComponent } from './components/first-name/first-name.component';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { CustomerRepeatTypeComponent } from './components/repeat/customer-repeat/customer-repeat.component';
import { CustomSelectboxComponent } from './components/selectbox/selectbox.component';
import { FormViewerCustomerSectionPanelWrapperComponent } from './components/wrappers/customer-section-wrapper/section-wrapper.component';
import { CustomTextComponent } from './components/text/text.component';
import { SafePipe } from './pipes/safe.pipe';
import { FORM_VIEWER_FEATURE_KEY, reducers } from './+state';
import { CustomerFormFacade } from './+state/customer-form/customer-form.facade';
import { CustomerFormEffects } from './+state/customer-form/customer-form.effects';
import { CustomCompanyNameComponent } from './components/company-name/company-name.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CustomGridComponent } from './components/grid/grid.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { CsvImporterModule } from '@xilo-mono/csv-importer';
import { CurrencyComponent } from './components/currency/currency.component';
import { NgxCurrencyModule } from 'ngx-currency';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CustomGridRowComponent } from './components/grid/row/row.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastService } from '@xilo-mono/form-contracts';
import { SelectionModalComponent } from './components/data-pull-modal/selection-modal/selection-modal.component';
import { LoadingModalComponent } from './components/data-pull-modal/loading-modal/loading-modal.component';
import { FailureModalComponent } from './components/data-pull-modal/failure-modal/failure-modal.component';
import { SDKPolicyModalComponent } from './components/data-pull-modal/sdk-policy-modal/sdk-policy-modal.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { CovieModalComponent } from './components/covie-modal/covie-modal.component';
import { CustomCalculatorComponent } from './components/calculator/calculator.component';
import { TableInputWrapperComponent } from './components/wrappers/table-input-wrapper/table-input-wrapper.component';
import { FormlyHotkeysWrapperComponent } from './components/wrappers/hotkeys-wrapper/hotkeys-wrapper.component';
import { XiloAccordionModule, XiloButtonModule, XiloDialogModule } from '@xilo/library';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { RepeatAddButtonPipe } from './pipes/repeat-add-button.pipe';
import { PullDataGroupTitlePipe } from './pipes/pull-data-group-title.pipe';
import { DragDropModule } from '@angular/cdk/drag-drop';

export const options: Partial<IConfig> | (() => Partial<IConfig>) = {};

export function minlengthValidationMessage(err, field) {
  return `Should have atleast ${field.templateOptions.minLength} characters`;
}

export function maxlengthValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions.maxLength} characters`;
}

export function minValidationMessage(err, field) {
  return `This value should be more than ${field.templateOptions.min}`;
}

export function maxValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions.max}`;
}

export function applyDefaultOptions(field) {
  if (field.key && !field.modelOptions) {
    field.modelOptions = {
      debounce: { default: 200 }
    };
  }
}

export function convertNullStringToUndefined(field) {
  if (field.defaultValue === 'null') {
    field.defaultValue = undefined;
  }
  return field;
}

@NgModule({
  declarations: [
    FormViewerComponent,
    FormIntakeTypeComponent,
    RepeatTypeComponent,
    FormViewerSectionPanelWrapperComponent,
    CustomSelectComponent,
    CustomerFormTypeComponent,
    CustomerSectionTypeComponent,
    IntakeSectionTypeComponent,
    QuestionGroupTypeComponent,
    AddressComponent,
    AddressV2Component,
    CustomInputComponent,
    PhoneNumberComponent,
    IntakeRepeatTypeComponent,
    CustomTextareaComponent,
    CustomTableComponent,
    CustomRadioComponent,
    CustomCheckboxComponent,
    CustomFirstNameComponent,
    CustomCompanyNameComponent,
    CustomLastNameComponent,
    FormViewerCustomerSectionPanelWrapperComponent,
    FormViewerQuestionPanelWrapperComponent,
    CustomerRepeatTypeComponent,
    CustomerSectionTypeComponent,
    CustomSelectboxComponent,
    CustomTextComponent,
    CustomCalculatorComponent,
    SafePipe,
    RepeatTitlePipe,
    CustomGridComponent,
    CustomGridRowComponent,
    FileUploadComponent,
    CurrencyComponent,
    SelectionModalComponent,
    LoadingModalComponent,
    FailureModalComponent,
    CovieModalComponent,
    SDKPolicyModalComponent,
    RepeatAddButtonPipe,
    PullDataGroupTitlePipe,
    FormlyHotkeysWrapperComponent
  ],
  imports: [
    CommonModule,
    MatDialogModule,
    MatExpansionModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    FontAwesomeModule,
    CsvImporterModule,
    NgxMaskModule.forRoot(options),
    NgxCurrencyModule,
    MatCheckboxModule,
    XiloDialogModule,
    MatProgressBarModule,
    XiloButtonModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatIconModule,
    FormlyModule.forRoot({
      types: [
        { name: 'repeat', component: RepeatTypeComponent },
        {
          name: 'intake-repeat',
          component: IntakeRepeatTypeComponent,
          defaultOptions: { defaultValue: [] }
        },
        {
          name: 'customer-repeat',
          component: CustomerRepeatTypeComponent,
          defaultOptions: { defaultValue: [] }
        },
        {
          name: 'customer-section',
          component: CustomerSectionTypeComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'intake-section',
          component: IntakeSectionTypeComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'customer-form',
          component: CustomerFormTypeComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'intake-form',
          component: FormIntakeTypeComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'question-group',
          component: QuestionGroupTypeComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'grid',
          component: CustomGridComponent,
          defaultOptions: { defaultValue: {} }
        },
        {
          name: 'row',
          component: CustomGridRowComponent,
          defaultOptions: { defaultValue: {} }
        },
        { name: 'address', component: AddressComponent, wrappers: ['hotkeys'] },
        { name: 'addressV2', component: AddressV2Component, wrappers: ['hotkeys'] },
        { name: 'input', component: CustomInputComponent, wrappers: ['hotkeys'] },
        { name: 'firstName', component: CustomFirstNameComponent, wrappers: ['hotkeys'] },
        { name: 'companyName', component: CustomCompanyNameComponent, wrappers: ['hotkeys'] },
        { name: 'lastName', component: CustomLastNameComponent, wrappers: ['hotkeys'] },
        { name: 'select', component: CustomSelectComponent, wrappers: ['hotkeys'] },
        { name: 'radio', component: CustomRadioComponent, wrappers: ['hotkeys'] },
        { name: 'checkbox', component: CustomCheckboxComponent, wrappers: ['hotkeys'] },
        { name: 'phonenumber', component: PhoneNumberComponent, wrappers: ['hotkeys'] },
        { name: 'currency', component: CurrencyComponent, wrappers: ['hotkeys'] },
        { name: 'textarea', component: CustomTextareaComponent, wrappers: ['hotkeys'] },
        { name: 'table', component: CustomTableComponent, wrappers: ['hotkeys'] },
        { name: 'selectbox', component: CustomSelectboxComponent, wrappers: ['hotkeys'] },
        { name: 'text', component: CustomTextComponent, wrappers: ['hotkeys'] },
        { name: 'calculator', component: CustomCalculatorComponent, wrappers: ['hotkeys'] },
        { name: 'file-upload', component: FileUploadComponent, wrappers: ['hotkeys'] }
      ],
      wrappers: [
        {
          name: 'customer-section-panel',
          component: FormViewerCustomerSectionPanelWrapperComponent
        },
        {
          name: 'question-panel',
          component: FormViewerQuestionPanelWrapperComponent
        },
        {
          name: 'section-panel',
          component: FormViewerSectionPanelWrapperComponent
        },
        { name: 'table-field', component: TableInputWrapperComponent },
        { name: 'hotkeys', component: FormlyHotkeysWrapperComponent }
      ],
      validationMessages: [
        { name: 'required', message: 'This field is required' },
        { name: 'minlength', message: minlengthValidationMessage },
        { name: 'maxlength', message: maxlengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage }
      ],
      extensions: [
        {
          name: 'defaultOptions',
          extension: {
            prePopulate: applyDefaultOptions
          }
        },
        {
          name: 'defaultValue',
          extension: {
            prePopulate: convertNullStringToUndefined
          }
        }
      ],
      extras: {
        checkExpressionOn: 'modelChange'
      }
    }),
    FormlySelectModule,
    GooglePlaceModule,
    StoreModule.forFeature(FORM_VIEWER_FEATURE_KEY, reducers, { metaReducers }),
    EffectsModule.forFeature([SubmissionEffects, CustomerFormEffects]),
    XiloAccordionModule,
    MatMenuModule,
    DatePlaceholderDirective,
    DragDropModule
  ],
  providers: [
    CustomerFormFacade,
    ToastService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
  ],
  exports: [FormViewerComponent]
})
export class FormViewerModule {}
